import React, { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import { useLeadDuplicates } from '@/api/leads/use-leads';
import DuplicateGroupCard from './DuplicateGroupCard';

interface LeadDuplicatesDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
}

const PER_PAGE = 10;

const LeadDuplicatesDialog: React.FC<LeadDuplicatesDialogProps> = ({ open, onOpenChange }) => {
    const [page, setPage] = useState(1);
    const { data, isLoading, isError } = useLeadDuplicates({ page, per_page: PER_PAGE }, open);

    useEffect(() => {
        if (open) setPage(1);
    }, [open]);

    // A merge can empty the last page; step back instead of showing nothing.
    const lastPage = data?.meta.last_page ?? 1;
    useEffect(() => {
        if (page > lastPage) setPage(lastPage);
    }, [page, lastPage]);

    const groups = data?.data ?? [];
    const total = data?.meta.total ?? 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="flex max-h-[90vh] max-w-4xl flex-col">
                <DialogHeader>
                    <DialogTitle>Duplicate leads</DialogTitle>
                    <DialogDescription>
                        Leads and customers that share an email or a phone number. Pick the record
                        to keep in each group: it keeps its own values and only its empty fields are
                        filled from the merged leads. Their notes, tasks and history move to it, and
                        they are then deleted. A lead is always merged into a customer, never the
                        other way round.
                    </DialogDescription>
                </DialogHeader>

                <div className="-mx-1 min-h-0 flex-1 space-y-3 overflow-y-auto px-1">
                    {isLoading ? (
                        [0, 1, 2].map(i => <Skeleton key={i} className="h-36 w-full" />)
                    ) : isError ? (
                        <p className="py-10 text-center text-sm text-destructive">
                            Could not load duplicate leads.
                        </p>
                    ) : groups.length === 0 ? (
                        <p className="py-10 text-center text-sm text-muted-foreground">
                            No duplicate leads found.
                        </p>
                    ) : (
                        groups.map(group => (
                            <DuplicateGroupCard
                                key={group.records.map(record => record.id).join(',')}
                                group={group}
                            />
                        ))
                    )}
                </div>

                {total > 0 && (
                    <div className="flex items-center justify-between border-t border-border pt-3 text-sm text-muted-foreground">
                        <span>
                            {total} duplicate group{total !== 1 ? 's' : ''}
                        </span>
                        {lastPage > 1 && (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p - 1)}
                                    disabled={page <= 1}
                                    aria-label="Previous page"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </Button>
                                <span>
                                    Page {page} of {lastPage}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => setPage(p => p + 1)}
                                    disabled={page >= lastPage}
                                    aria-label="Next page"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </Button>
                            </div>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default LeadDuplicatesDialog;
